import { useCallback, useEffect, useState } from 'react'
import { customerLinksApi, type CustomerLinkState, type CustomerLinkView } from '../api/customerLinks'
import { dateIt, errMsg } from '../lib/financeFormat'
import { useFeatureFlag } from '../lib/featureFlags'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, StatusPill, TextField, type PillTone } from './ui'

const STATE_TONE: Record<CustomerLinkState, PillTone> = {
  Attivo: 'info', Completato: 'success', Scaduto: 'neutral', Revocato: 'neutral', Bloccato: 'error',
}

/**
 * C-09 — pagina pratica: link sicuro per il cliente (conferma dei dati, documenti, bolletta, IBAN). Visibile solo con il
 * flag `portale_cliente`; un 403/404 la nasconde. L'indirizzo del link si vede una volta sola, appena creato: il server
 * conserva solo l'hash del token. Un nuovo link revoca quello ancora attivo.
 */
export function CustomerLinkPanel({ contractId }: { contractId: number }) {
  const enabled = useFeatureFlag('portale_cliente')
  const [items, setItems] = useState<CustomerLinkView[] | null>(null)
  const [hidden, setHidden] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [created, setCreated] = useState<{ url: string; expiresAt: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const [ibans, setIbans] = useState<Record<number, string>>({})

  const load = useCallback(() => {
    customerLinksApi.list(contractId)
      .then(setItems)
      .catch(e => { if (/403|404|forbidden|not found/i.test(errMsg(e, ''))) setHidden(true); else setError(errMsg(e, 'Link cliente non disponibili.')) })
  }, [contractId])

  useEffect(() => { if (enabled) load() }, [enabled, load])

  if (!enabled || hidden) return null

  async function create() {
    setBusy(true); setError(''); setCopied(false)
    try {
      const r = await customerLinksApi.create(contractId)
      setCreated({ url: r.url, expiresAt: r.expiresAt })
      load()
    } catch (e) { setError(errMsg(e, 'Creazione del link non riuscita.')) } finally { setBusy(false) }
  }

  async function revoke(id: number) {
    setBusy(true); setError('')
    try { await customerLinksApi.revoke(contractId, id); load() } catch (e) { setError(errMsg(e, 'Revoca non riuscita.')) } finally { setBusy(false) }
  }

  async function showIban(id: number) {
    try {
      const r = await customerLinksApi.iban(contractId, id)
      setIbans(s => ({ ...s, [id]: r.iban }))
    } catch (e) { setError(errMsg(e, 'IBAN non disponibile.')) }
  }

  async function copy() {
    if (!created) return
    try { await navigator.clipboard.writeText(created.url); setCopied(true) } catch { setCopied(false) }
  }

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        icon="link"
        title="Link sicuro per il cliente"
        subtitle="Il cliente conferma i dati, carica documenti e bolletta e indica l'IBAN, dopo un codice via email."
        actions={<Button variant="tonal" icon="add_link" loading={busy} onClick={() => void create()}>Nuovo link</Button>}
      />
      {(error || created) && <CardContent>
        {error && <Callout tone="error">{error}</Callout>}
        {created && (
          <Callout tone="success" title="Link creato: copialo ora, non sarà più visibile">
            <TextField label="Link per il cliente" value={created.url} readOnly onFocus={e => e.currentTarget.select()} />
            <div className="d-flex gap-2 align-items-center mt-2">
              <Button variant="outlined" icon="content_copy" onClick={() => void copy()}>{copied ? 'Copiato' : 'Copia'}</Button>
              <span className="text-muted md-typescale-body-small">Scade il {dateIt(created.expiresAt)}. Il link precedente, se c'era, è stato revocato.</span>
            </div>
          </Callout>
        )}
      </CardContent>}
      <DataTable variant="plain" aria-label="Link per il cliente">
        <thead>
          <tr><th>Creato</th><th>Stato</th><th>Scadenza</th><th className="text-end">File</th><th>Dati</th><th>IBAN</th><th /></tr>
        </thead>
        <tbody>
          {items && items.length === 0 && <DataTableEmptyRow colSpan={7} title="Nessun link creato." />}
          {items?.map(l => (
            <tr key={l.id}>
              <td>{dateIt(l.createdAt)}</td>
              <td><StatusPill tone={STATE_TONE[l.state]}>{l.state}</StatusPill></td>
              <td>{dateIt(l.expiresAt)}</td>
              <td className="text-end md-num">{l.uploadedCount}</td>
              <td>
                {l.dataConfirmed === null ? '—' : l.dataConfirmed ? 'Confermati' : 'Da correggere'}
                {l.corrections && <div className="text-muted md-typescale-body-small" style={{ whiteSpace: 'pre-wrap' }}>{l.corrections}</div>}
              </td>
              <td>
                {ibans[l.id] ?? l.ibanMasked ?? '—'}
                {l.ibanMasked && !ibans[l.id] && (
                  <Button variant="text" size="sm" onClick={() => void showIban(l.id)}>Mostra</Button>
                )}
              </td>
              <td className="text-end">
                {l.state === 'Attivo' && <Button variant="text" tone="error" disabled={busy} onClick={() => void revoke(l.id)}>Revoca</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Card>
  )
}
